import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = "https://elshowdelasbanqueras.com/show-banqueras-api";

// Candidatos
const SEEKERS_URL = `${API_BASE}/api/job_seekers_list.php`;
const CONTACT_SEEKER_URL = `${API_BASE}/api/contact_profile.php`;
const LIKE_URL = `${API_BASE}/api/job_seekers_like.php`;

// Ofertas
const POSTS_URL = `${API_BASE}/api/job_posts_list.php`;

// ✅ NUEVO: contactar una oferta
const CONTACT_POST_URL = `${API_BASE}/api/contact_post.php`;

const PROVINCIAS = [
  "Azua","Baoruco","Barahona","Dajabón","Distrito Nacional","Duarte","Elías Piña","El Seibo","Espaillat",
  "Hato Mayor","Hermanas Mirabal","Independencia","La Altagracia","La Romana","La Vega","María Trinidad Sánchez",
  "Monseñor Nouel","Monte Cristi","Monte Plata","Pedernales","Peravia","Puerto Plata","Samaná","San Cristóbal",
  "San José de Ocoa","San Juan","San Pedro de Macorís","Sánchez Ramírez","Santiago","Santiago Rodríguez",
  "Santo Domingo","Valverde"
];

function likedKey(profileId) {
  const today = new Date().toISOString().slice(0, 10);
  return `sbd_like_${profileId}_${today}`;
}

const typeStyle = (type) => {
  if (type === "post") return { border: "2px solid rgba(255, 105, 180, .55)" }; // rosado
  return { border: "2px solid rgba(59, 130, 246, .55)" }; // azul
};

export default function Jobs() {
  const [seekers, setSeekers] = useState([]);
  const [posts, setPosts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Provincia filtro + stats
  const [provincia, setProvincia] = useState("");
  const [countsByProv, setCountsByProv] = useState({});
  const [countsSeekersByProv, setCountsSeekersByProv] = useState({});
  const [countsPostsByProv, setCountsPostsByProv] = useState({});

  // ===== Modal Contact (candidato / oferta) =====
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // item seleccionado (seeker o post)
  const [contactType, setContactType] = useState(null); // "seeker" | "post"

  const [contactForm, setContactForm] = useState({
    tu_nombre: "",
    tu_numero: "",
    mensaje: "",
  });

  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [sentErr, setSentErr] = useState("");

  // ===== Like =====
  const [likingId, setLikingId] = useState(null);
  const [likeErr, setLikeErr] = useState("");

  // Helpers
  const buildUrl = (base, prov) => {
    const url = new URL(base);
    if (prov) url.searchParams.set("provincia", prov);
    return url.toString();
  };

  const computeCounts = (seekerRows, postRows) => {
    const all = {};
    const sMap = {};
    const pMap = {};

    for (const r of seekerRows) {
      const prov = (r.provincia || "").trim();
      if (!prov) continue;
      sMap[prov] = (sMap[prov] || 0) + 1;
      all[prov] = (all[prov] || 0) + 1;
    }
    for (const r of postRows) {
      const prov = (r.provincia || "").trim();
      if (!prov) continue;
      pMap[prov] = (pMap[prov] || 0) + 1;
      all[prov] = (all[prov] || 0) + 1;
    }

    setCountsSeekersByProv(sMap);
    setCountsPostsByProv(pMap);
    setCountsByProv(all);
  };

  const safeJson = async (res, label) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`La API no devolvió JSON válido (${label}).`);
    }
  };

  // Fetch (candidatos + ofertas)
  const fetchAll = async (prov = provincia) => {
    setLoading(true);
    setErr("");
    setLikeErr("");

    try {
      // 1) candidatos
      const seekersRes = await fetch(buildUrl(SEEKERS_URL, prov));
      const seekersData = await safeJson(seekersRes, "job_seekers_list.php");
      if (!seekersRes.ok) throw new Error(seekersData?.message || "Error cargando candidatos");
      const seekersRows = Array.isArray(seekersData) ? seekersData : [];

      // 2) ofertas
      let postsRows = [];
      try {
        const postsRes = await fetch(buildUrl(POSTS_URL, prov));
        const postsData = await safeJson(postsRes, "job_posts_list.php");
        if (postsRes.ok) postsRows = Array.isArray(postsData) ? postsData : [];
        else console.warn("job_posts_list.php error:", postsData);
      } catch (e) {
        console.warn("No se pudo cargar ofertas:", e?.message);
      }

      setSeekers(seekersRows);
      setPosts(postsRows);

      computeCounts(seekersRows, postsRows);
    } catch (e) {
      setErr(e.message || "Error cargando datos");
      setSeekers([]);
      setPosts([]);
      setCountsByProv({});
      setCountsSeekersByProv({});
      setCountsPostsByProv({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll(provincia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provincia]);

  const provinciaCountLabel = useMemo(() => {
    if (!provincia) return "";
    const s = countsSeekersByProv[provincia] || 0;
    const p = countsPostsByProv[provincia] || 0;
    const total = countsByProv[provincia] || 0;
    return `${total} publicaciones • ${s} candidatos • ${p} ofertas`;
  }, [provincia, countsByProv, countsSeekersByProv, countsPostsByProv]);

  // ========================
  // MODAL CONTACTO
  // ========================
  const openContact = (item, type) => {
    setSelected(item);
    setContactType(type); // "seeker" | "post"
    setSentOk(false);
    setSentErr("");
    setContactForm({ tu_nombre: "", tu_numero: "", mensaje: "" });
    setOpen(true);
  };

  const closeContact = () => {
    setOpen(false);
    setSelected(null);
    setContactType(null);
  };

  const onChangeContact = (e) => {
    setContactForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const onSubmitContact = async (e) => {
    e.preventDefault();
    setSentOk(false);
    setSentErr("");

    if (!selected || !contactType) return;

    if (!contactForm.tu_nombre.trim() || !contactForm.tu_numero.trim() || !contactForm.mensaje.trim()) {
      setSentErr("Completa tu nombre, tu número y el mensaje.");
      return;
    }

    setSending(true);
    try {
      const isSeeker = contactType === "seeker";
      const url = isSeeker ? CONTACT_SEEKER_URL : CONTACT_POST_URL;

      const payload = isSeeker
        ? {
            profile_id: selected.id,
            profile_nombre: selected.nombre,
            profile_area: selected.area,
            profile_provincia: selected.provincia || "",
            tu_nombre: contactForm.tu_nombre.trim(),
            tu_numero: contactForm.tu_numero.trim(),
            mensaje: contactForm.mensaje.trim(),
          }
        : {
            post_id: selected.id,
            empresa: selected.empresa || "",
            puesto: selected.puesto || "",
            provincia: selected.provincia || "",
            ciudad: selected.ciudad || "",
            tu_nombre: contactForm.tu_nombre.trim(),
            tu_numero: contactForm.tu_numero.trim(),
            mensaje: contactForm.mensaje.trim(),
          };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await safeJson(res, isSeeker ? "contact_profile.php" : "contact_post.php");
      if (!res.ok) throw new Error(data?.message || "No se pudo enviar el mensaje.");

      setSentOk(true);
      setTimeout(() => closeContact(), 1400);
    } catch (e2) {
      setSentErr(e2.message || "Error enviando el mensaje.");
    } finally {
      setSending(false);
    }
  };

  // ========================
  // LIKE / RATING (solo candidatos)
  // ========================
  const canLike = (profileId) => {
    try {
      return !localStorage.getItem(likedKey(profileId));
    } catch {
      return true;
    }
  };

  const markLiked = (profileId) => {
    try {
      localStorage.setItem(likedKey(profileId), "1");
    } catch {}
  };

  const onLike = async (profile) => {
    setLikeErr("");
    if (!profile?.id) return;

    if (!canLike(profile.id)) {
      setLikeErr("Ya recomendaste hoy a este perfil.");
      return;
    }

    setLikingId(profile.id);
    try {
      const res = await fetch(LIKE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_seeker_id: profile.id }),
      });

      const data = await safeJson(res, "job_seekers_like.php");
      if (!res.ok) throw new Error(data?.message || "No se pudo recomendar.");

      const newLikes =
        typeof data.likes_count === "number"
          ? data.likes_count
          : (profile.likes_count || 0) + 1;

      setSeekers((prev) =>
        prev.map((p) => (p.id === profile.id ? { ...p, likes_count: newLikes } : p))
      );

      markLiked(profile.id);
    } catch (e) {
      setLikeErr(e.message || "Error recomendando perfil.");
    } finally {
      setLikingId(null);
    }
  };

  // ========================
  // UI data: combinar candidatos + ofertas
  // ========================
  const combined = useMemo(() => {
    const seekerItems = seekers.map((s) => ({ type: "seeker", ...s }));
    const postItems = posts.map((p) => ({ type: "post", ...p }));

    // orden: ofertas primero, luego candidatos por likes
    const sortedSeekers = [...seekerItems].sort(
      (a, b) => Number(b.likes_count || 0) - Number(a.likes_count || 0)
    );

    return [...postItems, ...sortedSeekers];
  }, [seekers, posts]);

  return (
    <main className="container">
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 6 }}>Empleo</h2>
            <p className="p" style={{ marginTop: 0 }}>
              Parrilla combinada: <b style={{ color: "var(--gold)" }}>Candidatos</b> (borde azul) y{" "}
              <b style={{ color: "var(--gold)" }}>Ofertas</b> (borde rosa).
              El teléfono no es público. Para contactar, envía tu número y te ponemos en contacto.
            </p>
          </div>

          <button className="btn btn-ghost" onClick={() => fetchAll(provincia)} disabled={loading}>
            {loading ? "Cargando..." : "Actualizar"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
          <Link className="btn btn-blue" to="/empleo/busca">¿Buscas empleo?</Link>
          <Link className="btn btn-pink" to="/empleo/contratando">Estamos contratando</Link>

          <div style={{ flex: 1, minWidth: 220 }} />

          <div style={{ minWidth: 320 }}>
            <label style={{ display: "block", fontWeight: 900, marginBottom: 6 }}>
              Buscar por provincia {provincia ? `• ${provinciaCountLabel}` : ""}
            </label>
            <select
              className="card"
              style={{ width: "100%", padding: 12 }}
              value={provincia}
              onChange={(e) => setProvincia(e.target.value)}
            >
              <option value="">Todas las provincias</option>
              {PROVINCIAS.map((p) => (
                <option key={p} value={p}>
                  {p}
                  {countsByProv[p]
                    ? ` (Total: ${countsByProv[p]} | C: ${countsSeekersByProv[p] || 0} | O: ${countsPostsByProv[p] || 0})`
                    : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {err && <div style={{ marginTop: 12, color: "#ff7b7b" }}>❌ {err}</div>}
        {likeErr && <div style={{ marginTop: 12, color: "#ffb86b" }}>⚠️ {likeErr}</div>}
      </div>

      <div className="grid grid-3" style={{ marginTop: 16 }}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div className="card" key={i} style={{ opacity: 0.7 }}>
              <b style={{ color: "var(--gold)" }}>Cargando...</b>
              <p className="p" style={{ marginBottom: 0 }}>Obteniendo publicaciones...</p>
            </div>
          ))
        ) : combined.length === 0 ? (
          <div className="card" style={{ gridColumn: "1 / -1" }}>
            <b style={{ color: "var(--gold)" }}>No hay publicaciones en esta provincia</b>
            <p className="p" style={{ marginBottom: 0 }}>
              Prueba con “Todas las provincias” o publica desde “¿Buscas empleo?” / “Estamos contratando”
            </p>
          </div>
        ) : (
          combined.map((item) => {
            // =========================
            // CARD: CANDIDATO
            // =========================
            if (item.type === "seeker") {
              const fotoPath = (item.foto_url || "").replace(/^\/+/, "");
              const fotoSrc = fotoPath ? `${API_BASE}/${fotoPath}` : "";
              const likes = Number.isFinite(Number(item.likes_count)) ? Number(item.likes_count) : 0;
              const likedToday = !canLike(item.id);

              return (
                <div className="card" key={`seeker-${item.id}`} style={{ overflow: "hidden", ...typeStyle("seeker") }}>
                  <div
                    style={{
                      width: "100%",
                      height: 160,
                      borderRadius: 16,
                      background: "rgba(255,255,255,.06)",
                      overflow: "hidden",
                      display: "grid",
                      placeItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    {fotoSrc ? (
                      <img src={fotoSrc} alt={item.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                    ) : (
                      <span style={{ opacity: 0.7 }}>Sin foto</span>
                    )}
                  </div>

                  <div style={{ fontWeight: 900 }}>
                    {item.nombre} <span style={{ color: "var(--gold)" }}>• {item.area}</span>
                  </div>

                  <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                    📍 {item.ciudad} {item.provincia ? `• 🏛️ ${item.provincia}` : ""}
                  </div>

                  <p className="p" style={{ marginTop: 10 }}>{item.perfil}</p>

                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button className="btn btn-blue" onClick={() => openContact(item, "seeker")} style={{ flex: 1 }}>
                      Contactar
                    </button>

                    <button
                      className="btn btn-ghost"
                      onClick={() => onLike(item)}
                      disabled={likingId === item.id || likedToday}
                      title={likedToday ? "Ya recomendaste hoy" : "Recomendar candidato"}
                      style={{ minWidth: 150, justifyContent: "center", opacity: likingId === item.id || likedToday ? 0.7 : 1 }}
                    >
                      {likingId === item.id ? "..." : likedToday ? `👍 ${likes}` : `👍 Recomendar (${likes})`}
                    </button>
                  </div>

                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>{item.created_at}</div>
                </div>
              );
            }

            // =========================
            // CARD: OFERTA
            // =========================
            const cupos = Number.isFinite(Number(item.cupos)) ? Number(item.cupos) : null;

            const fotoPath = (item.foto_url || "").replace(/^\/+/, "");
            const fotoSrc = fotoPath ? `${API_BASE}/${fotoPath}` : "";

            return (
              <div className="card" key={`post-${item.id}`} style={{ overflow: "hidden", ...typeStyle("post") }}>
                {fotoSrc ? (
                  <div style={{ height: 150, borderRadius: 14, overflow: "hidden", marginBottom: 10 }}>
                    <img src={fotoSrc} alt={item.empresa || "Empresa"} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                  </div>
                ) : null}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <b style={{ color: "var(--gold)" }}>{item.puesto || "Oferta de empleo"}</b>
                  <span style={{ fontSize: 12, opacity: 0.75 }}>
                    {cupos !== null ? `Cupos: ${cupos}` : "Empresa"}
                  </span>
                </div>

                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
                  🏢 {item.empresa || "Empresa"} • 📍 {item.ciudad || "—"} {item.provincia ? `• 🏛️ ${item.provincia}` : ""}
                </div>

                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
                  {item.horario ? `🕒 ${item.horario}` : "🕒 —"}
                  {item.tipo_puesto ? ` • 💼 ${item.tipo_puesto}` : ""}
                  {item.sueldo ? ` • 💰 ${item.sueldo}` : ""}
                  {Number(item.beneficios_ley) === 1 ? " • ✅ Beneficios de ley" : ""}
                </div>

                <p className="p" style={{ marginTop: 10 }}>
                  {/* tu backend puede devolver requisitos o descripcion */}
                  {item.descripcion || item.requisitos || ""}
                </p>

                <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                  <button
                    className="btn btn-pink"
                    onClick={() => openContact(item, "post")}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    Contactar / Aplicar
                  </button>
                </div>

                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>
                  {item.created_at || ""}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* MODAL CONTACTO (candidato u oferta) */}
      {open && selected && contactType && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeContact}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(560px, 100%)", borderRadius: 18 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>
                  {contactType === "seeker" ? (
                    <>Contactar a <span style={{ color: "var(--gold)" }}>{selected.nombre}</span></>
                  ) : (
                    <>Contactar oferta: <span style={{ color: "var(--gold)" }}>{selected.puesto}</span></>
                  )}
                </h3>

                <div style={{ opacity: 0.8, fontSize: 13 }}>
                  {contactType === "seeker" ? (
                    <>Área: {selected.area} • {selected.ciudad} {selected.provincia ? `• ${selected.provincia}` : ""}</>
                  ) : (
                    <>Empresa: {selected.empresa} • {selected.ciudad} {selected.provincia ? `• ${selected.provincia}` : ""}</>
                  )}
                </div>
              </div>

              <button className="btn btn-ghost" onClick={closeContact}>Cerrar</button>
            </div>

            <div style={{ height: 10 }} />

            <form onSubmit={onSubmitContact}>
              <label style={{ fontWeight: 800, display: "block", marginBottom: 6 }}>Tu nombre</label>
              <input
                className="card"
                style={{ width: "100%", padding: 12 }}
                name="tu_nombre"
                value={contactForm.tu_nombre}
                onChange={onChangeContact}
                placeholder="Ej: Juan Pérez"
                required
              />

              <div style={{ height: 10 }} />

              <label style={{ fontWeight: 800, display: "block", marginBottom: 6 }}>Tu número (WhatsApp)</label>
              <input
                className="card"
                style={{ width: "100%", padding: 12 }}
                name="tu_numero"
                value={contactForm.tu_numero}
                onChange={onChangeContact}
                placeholder="Ej: 809-000-0000"
                required
              />

              <div style={{ height: 10 }} />

              <label style={{ fontWeight: 800, display: "block", marginBottom: 6 }}>Mensaje</label>
              <textarea
                className="card"
                style={{ width: "100%", padding: 12, minHeight: 110 }}
                name="mensaje"
                value={contactForm.mensaje}
                onChange={onChangeContact}
                placeholder={
                  contactType === "seeker"
                    ? "Escribe por qué deseas contactar a este candidato..."
                    : "Hola, estoy interesado/a en esta vacante. Mi experiencia es..."
                }
                required
              />

              <div style={{ height: 12 }} />

              <button
                className={contactType === "seeker" ? "btn btn-blue" : "btn btn-pink"}
                type="submit"
                disabled={sending}
                style={{ width: "100%", justifyContent: "center", opacity: sending ? 0.7 : 1 }}
              >
                {sending ? "Enviando..." : "Enviar solicitud"}
              </button>

              {sentErr && <div style={{ marginTop: 12, color: "#ff7b7b" }}>❌ {sentErr}</div>}
              {sentOk && <div style={{ marginTop: 12, color: "var(--green)" }}>✅ Solicitud enviada</div>}

              <div style={{ fontSize: 12, opacity: 0.65, marginTop: 10 }}>
                Por seguridad, tu número no se publica. Nosotros enviamos tu solicitud al destino correspondiente.
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
